import { useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { applyConversationMessageToCache } from "../lib/conversationSocketCache";
import { useWebsocket } from "@finitless/shared/contexts";

/**
 * Hook for handling real-time conversation messages via websockets
 * Specifically designed for conversation detail pages
 */
export const useConversationMessagesSocket = (conversationId) => {
  const queryClient = useQueryClient();
  const { isConnected, registerEventListener, unregisterEventListener } =
    useWebsocket();
  const [listenersSetup, setListenersSetup] = useState(false);
  const listenersRegistered = useRef(false);
  const hookId = useRef(
    `useConversationMessagesSocket-${Date.now()}-${Math.random()}`,
  );

  /**
   * Handle new conversation messages
   */
  const handleNewConversationMessage = useCallback(
    (messageData) => {
      if (
        messageData?.conversation_id == null ||
        String(messageData.conversation_id) !== String(conversationId)
      )
        return;
      applyConversationMessageToCache(queryClient, messageData);
    },
    [queryClient, conversationId],
  );

  /**
   * Handle conversation updates
   */
  const handleConversationUpdate = useCallback(
    (conversationData) => {
      // Only process updates for the current conversation
      if (conversationData.id !== conversationId) {
        return;
      }

      // Update individual conversation query
      queryClient.setQueriesData(
        { queryKey: ["conversation", conversationId] },
        (oldConversation) => {
          if (!oldConversation) return oldConversation;

          return {
            ...oldConversation,
            ...conversationData,
          };
        },
      );

      // Update conversations list
      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) return oldData;

        const updatedConversations = oldData.conversations.map(
          (conversation) =>
            conversation.id === conversationId
              ? { ...conversation, ...conversationData }
              : conversation,
        );

        return {
          ...oldData,
          conversations: updatedConversations,
        };
      });
    },
    [queryClient, conversationId],
  );

  // Use refs to maintain stable references
  const handleNewConversationMessageRef = useRef(handleNewConversationMessage);
  const handleConversationUpdateRef = useRef(handleConversationUpdate);

  // Update refs when handlers change
  useEffect(() => {
    handleNewConversationMessageRef.current = handleNewConversationMessage;
    handleConversationUpdateRef.current = handleConversationUpdate;
  }, [handleNewConversationMessage, handleConversationUpdate]);

  /**
   * Setup socket listeners using centralized event system
   */
  const setupSocketListeners = useCallback(() => {
    if (!isConnected || !conversationId || listenersRegistered.current) return;
    registerEventListener(
      "conversation.message.created",
      (data) => handleNewConversationMessageRef.current(data),
      hookId.current,
    );
    registerEventListener(
      "conversation.updated",
      (data) => handleConversationUpdateRef.current(data),
      hookId.current,
    );
    listenersRegistered.current = true;
    setListenersSetup(true);
  }, [isConnected, conversationId, registerEventListener]);

  const cleanupSocketListeners = useCallback(() => {
    if (!listenersRegistered.current) return;
    unregisterEventListener("conversation.message.created", hookId.current);
    unregisterEventListener("conversation.updated", hookId.current);
    listenersRegistered.current = false;
    setListenersSetup(false);
  }, [unregisterEventListener]);

  useEffect(() => {
    setupSocketListeners();
    return cleanupSocketListeners;
  }, [setupSocketListeners, cleanupSocketListeners]);

  return {
    isConnected,
    listenersSetup,
    conversationId,
  };
};

export default useConversationMessagesSocket;
